import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import classNames from 'classnames';
import { pick } from 'es-toolkit/compat';
import type { ReactElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';

import type { RootDispatch, RootState } from '../../../../configuration/setup/store';
import { ProductTypedFormattedMessage } from '../../common/product/ProductTypedFormattedMessage';
import type { ProductType } from '../../common/product/product';
import { termedLevelRenewalActions } from '../../common/termedLevel/redux/termedLevelRenewal.redux';
import type { TermedLevelId } from '../../common/termedLevel/redux/types';
import { isTermedLevelSelectable } from '../../common/termedLevel/termedLevelSelectionValidationService';
import type { TermedLevel } from '../../common/termedLevel/types';
import { getProductLabelFromContract } from '../../common/utils/productLabelUtil';
import { isManNowProduct } from '../../config/manNowProducts';
import { ContractDuration } from './ContractDuration';
import { ContractResourceName } from './ContractResourceName';
import { ContractState } from './ContractState';
import { ContractTableRowActionsCellContainer } from './ContractTableRowActionsCell';
import { ContractTableRowRenewalText } from './ContractTableRowRenewalText';
import { contractActions, getSelectedContracts } from './redux/contract.redux';

interface ContractTableColumn {
    id: string;
    propertyName?: keyof TermedLevel;
    label: ReactElement;
    width?: string;
}

export const getColumns = (productType: ProductType): ContractTableColumn[] => [
    {
        id: 'resourceName',
        propertyName: 'resourceName',
        label: (
            <ProductTypedFormattedMessage
                productType={productType}
                userBasedId='intl-msg:marketplace.contracts.user.table.header'
                assetBasedId='intl-msg:marketplace.contracts.asset.table.header'
            />
        ),
    },
    {
        id: 'productLevelName',
        propertyName: 'productLevelName',
        label: <FormattedMessage id={'intl-msg:common-message.contracts.type'} />,
    },
    {
        id: 'periodLength',
        propertyName: 'periodLength',
        label: <FormattedMessage id={'intl-msg:common-message.contracts.period'} />,
        width: '15%',
    },
    {
        id: 'remainingDays',
        propertyName: 'remainingDays',
        label: <FormattedMessage id={'intl-msg:common-message.contracts.remainingDays'} />,
        width: '10%',
    },
    {
        id: 'contractState',
        propertyName: 'contractState',
        label: <FormattedMessage id={'intl-msg:common-message.contracts.state'} />,
        width: '15%',
    },
    {
        id: 'renewsToLevelName',
        propertyName: 'renewsToLevelName',
        label: <FormattedMessage id={'intl-msg:common-message.contracts.renewal'} />,
    },
    { id: 'actions', label: <FormattedMessage id={'intl-msg:common-message.contracts.actions'} />, width: '10%' },
];

interface Props {
    contract: TermedLevel;
    updateContract: (data: TermedLevelId & { renewsToLevel: string | undefined }) => void;
    isContractSelected: boolean;
    toggleSelectedContract: (contractId: TermedLevelId) => void;
    serviceName: string;
}

export function ContractTableRow(props: Props) {
    const { contract, updateContract, isContractSelected, toggleSelectedContract, serviceName } = props;
    const isManNow = isManNowProduct(contract.productId);

    const toggleContract = () => {
        if (!isManNow && isTermedLevelSelectable(contract)) {
            toggleSelectedContract(pick(contract, ['productId', 'resourceId']));
        }
    };

    const onClickRenewalButton = (e: React.MouseEvent) => {
        e.stopPropagation();
        updateContract({
            productId: contract.productId,
            resourceId: contract.resourceId,
            renewsToLevel: contract.renewsToLevel,
        });
    };

    return (
        <tr
            onClick={(e: React.MouseEvent<HTMLTableRowElement>) => {
                if (!e.currentTarget.contains(e.target as Node)) {
                    return;
                }
                e.preventDefault();
                toggleContract();
            }}
        >
            <td>
                {!isManNow && isTermedLevelSelectable(contract) && (
                    <Checkbox
                        checked={isContractSelected}
                        onClick={(e: React.SyntheticEvent) => {
                            e.stopPropagation();
                            toggleContract();
                        }}
                    />
                )}
            </td>
            <td>
                <ContractResourceName contract={contract} />
            </td>
            <td>{getProductLabelFromContract(contract, useIntl())}</td>
            <td>
                <ContractDuration contract={contract} />
            </td>
            <td>
                {contract.remainingDays === 0 ? (
                    <span className='text-color-warning text-medium'>
                        <FormattedMessage id='intl-msg:marketplace.today' />
                    </span>
                ) : (
                    <span className={classNames({ 'text-color-warning text-medium': contract.criticalRemainingDays })}>
                        {contract.remainingDays}{' '}
                        <FormattedMessage
                            id={
                                contract.remainingDays === 1
                                    ? 'intl-msg:marketplace.durationUnit.day'
                                    : 'intl-msg:marketplace.durationUnit.days'
                            }
                        />
                    </span>
                )}
            </td>
            <td>
                <ContractState contractState={contract.contractState} />
            </td>
            <td>
                <ContractTableRowRenewalText contract={contract} />
            </td>
            <td className='text-right'>
                <ContractTableRowActionsCellContainer
                    contract={contract}
                    onClick={onClickRenewalButton}
                    serviceName={serviceName}
                />
            </td>
        </tr>
    );
}

const mapStateToProps = (state: RootState, ownProps: { contract: TermedLevel }) => ({
    isContractSelected:
        getSelectedContracts(state).find(
            contractId =>
                ownProps.contract.productId === contractId.productId &&
                ownProps.contract.resourceId === contractId.resourceId
        ) !== undefined,
});

const mapDispatchToProps = (dispatch: RootDispatch) => ({
    updateContract: (data: TermedLevelId & { renewsToLevel: string | undefined }) => {
        const { renewsToLevel, ...contractId } = data;
        dispatch(
            termedLevelRenewalActions.selectForUpdate({
                contracts: [contractId],
                renewsToLevel,
                productId: data.productId,
            })
        );
    },
    toggleSelectedContract: (contractId: TermedLevelId) => dispatch(contractActions.toggleSelectedContract(contractId)),
});

export const ContractTableRowContainer = connect(mapStateToProps, mapDispatchToProps)(ContractTableRow);
